import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {EquitySecurity} from "@/integrations/supabase/equity.models.ts";

class EquitySecuritiesService extends BaseService {
    /**
     * Creates an equity security for a given user.
     * @param {EquitySecurityCreateRequest} request
     */
    async createEquitySecurity(request: EquitySecurityCreateRequest): Promise<EquitySecurity> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('equity_securities')
            .insert(payload)
            .select()
            .single();
        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data;
    }

    /**
     * Fetch an equity security for a given user.
     * @param {EquitySecurityByIdRequest} request request to fetch equity security
     */
    async fetchEquitySecurityById(request: EquitySecurityByIdRequest): Promise<EquitySecurity> {
        const { data, error } = await supabase
            .from('equity_securities')
            .select('*')
            .eq('user_id', request.userId)
            .eq('id', request.id)
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetch all equity securities for a given user.
     * @param {string} userId User ID
     */
    async fetchAllEquitySecurities(userId: string): Promise<EquitySecurity[]> {
        const { data, error } = await supabase
            .from('equity_securities')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Updates equity sec for a given user.
     * @param {EquitySecurityUpdateRequest} request request to update equity security
     * @returns {Promise<EquitySecurity>} updated equity security
     */
    async updateEquitySecurity(request: EquitySecurityUpdateRequest): Promise<EquitySecurity> {
        const payload = this.convertRequestToSnakeCase(request);

        const { data, error } = await supabase
            .from('equity_securities')
            .update(payload)
            .eq('id', request.id)
            .eq('user_id', request.userId)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data;
    }

    async deleteEquitySecurity(request: EquitySecurityDeleteRequest): Promise<void> {
        const { error } = await supabase
            .from('equity_securities')
            .delete()
            .eq('id', request.id)
            .eq("user_id", request.userId);

        if (error) {
            await this.handleError(error);
            throw error;
        }
    }
}

export default new EquitySecuritiesService();